import React, { useLayoutEffect, useRef, useState } from 'react';
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import { ventureReel } from '../../../data/ventureStory';
const ease = [0.22, 1, 0.36, 1] as const;

// A pinned filmstrip: scrolling down slides the frames sideways, each image drifting inside its frame
export function HorizontalReel() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [distance, setDistance] = useState(0);
  useLayoutEffect(() => {
    const measure = () => {
      const track = trackRef.current;
      if (track) setDistance(Math.max(0, track.scrollWidth - window.innerWidth));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end end']
  });
  const smooth = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 24,
    mass: 0.6
  });
  const x = useTransform(smooth, [0, 1], [0, -distance]);
  const drift = useTransform(smooth, [0, 1], reduce ? ['0%', '0%'] : ['-6%', '6%']);
  const bar = useTransform(smooth, [0, 1], [0, 1]);
  return <section ref={ref} aria-labelledby="reel-heading" className="relative" style={{
    height: `calc(100vh + ${distance}px)`
  }} data-id="element-1297" data-mp="173hlqq3hsztr">
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden" data-id="element-1298" data-mp="1gvnlilzzklji">
        <div className="mb-8 flex items-end justify-between px-6 md:mb-10 md:px-16" data-id="element-1299" data-mp="1kwz78gfqommf">
          <h2 id="reel-heading" className="font-editorial text-[36px] leading-[40px] tracking-[-0.015em] md:text-[52px] md:leading-[54px]" data-id="element-1300" data-mp="1kytsjvuxagr1">
            {ventureReel.heading}
          </h2>
          <span className="hidden text-[13px] font-light text-paper/55 md:block" data-id="element-1301" data-mp="1ut2f82x9vfha">{ventureReel.note}</span>
        </div>

        <motion.div ref={trackRef} style={{
        x: reduce ? 0 : x
      }} className="flex w-max items-end gap-6 px-6 md:gap-10 md:px-16" data-id="element-1302" data-mp="h49iay691q5">
          {ventureReel.frames.map((f, i) => <motion.figure key={f.title} initial={{
          opacity: 0,
          y: reduce ? 0 : 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 1,
          ease,
          delay: i * 0.05
        }} className={`group relative shrink-0 ${f.shape === 'wide' ? 'w-[82vw] md:w-[52vw]' : 'w-[62vw] md:w-[28vw]'}`} data-id="element-1303" data-mp="3onwsbfybrfkc" data-mpi={i}>
              <div className={`relative overflow-hidden shadow-[0_50px_100px_-30px_rgba(0,0,0,0.85)] ${f.shape === 'wide' ? 'aspect-[16/10]' : 'aspect-[3/4]'}`} data-id="element-1304" data-mp="7dowwzndkvkp">
                <div className="absolute inset-0 transition-transform duration-[1400ms] ease-luxe group-hover:scale-[1.05]" data-id="element-1305" data-mp="g5qy5059qfw1">
                  <motion.img src={f.image} alt={f.alt} style={{
                x: drift
              }} className="absolute inset-y-0 left-[-8%] h-full w-[116%] max-w-none object-cover" data-id="element-1306" data-mp="v3syl1h06l92" />
                </div>
                <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,0.6),rgba(0,0,0,0)_45%)] opacity-70 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" data-id="element-1307" data-mp="1dqk2hk8dk5yf" />
                <span className="absolute left-5 top-5 font-editorial text-[44px] leading-none text-paper/30 transition-colors duration-500 group-hover:text-paper/70 md:text-[64px]" aria-hidden="true" data-id="element-1308" data-mp="14pu5webaibjn">
                  {f.time}
                </span>
              </div>
              <figcaption className="mt-5 flex items-baseline gap-4" data-id="element-1309" data-mp="h1ujtroxb9be">
                <span className="text-[13px] font-light text-paper/55" data-id="element-1310" data-mp="gzhrckljhgk3">{f.label}</span>
                <span className="text-[18px] font-light text-paper transition-transform duration-500 ease-luxe group-hover:translate-x-1 md:text-[20px]" data-id="element-1311" data-mp="lyb76joatjnf">{f.title}</span>
              </figcaption>
            </motion.figure>)}
        </motion.div>

        <div className="mx-6 mt-10 h-px bg-paper/15 md:mx-16" data-id="element-1312" data-mp="1fdeywfhz6rrw">
          <motion.div style={{
          scaleX: bar
        }} className="h-px origin-left bg-paper/60" data-id="element-1313" data-mp="1ae2ctwyxhoqd" />
        </div>
      </div>
    </section>;
}